import React, { useEffect, useState } from 'react';
import { Search, Plus, Edit, Trash2, AlertTriangle, Package, History } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import {
  createInventoryItem,
  deleteInventoryItem,
  getInventory,
  getInventoryMovements,
  updateInventoryItem,
} from '../../services/api';
import { calculateMargin, formatMoney, formatQuantity, isLowStock } from '../../lib/inventory';

const categories = [
  { value: 'pipes', label: 'Tuyaux' },
  { value: 'fittings', label: 'Raccords' },
  { value: 'equipment', label: 'Équipement' },
  { value: 'tools', label: 'Outils' },
  { value: 'parts', label: 'Pièces' },
  { value: 'other', label: 'Autre' },
];

const EMPTY_FORM = {
  name: '', supplier: '', unit: 'unité', quantity: '0', purchase_price: '0.00',
  sale_price: '0.00', category: 'parts', min_stock_level: '5', reorder_target: '10',
};

export const InventoryPage = () => {
  const { can } = useAuth();
  const canManage = can('inventory.manage');
  const canDelete = can('inventory.delete');
  const canSeeCosts = can('inventory.costs');
  const canSeeMovements = can('inventory.movements.read');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [movementItem, setMovementItem] = useState(null);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const fetchInventory = async () => {
    try {
      const response = await getInventory();
      setItems(response.data || []);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchInventory(); }, []);

  const validate = () => {
    const numericFields = ['quantity', 'purchase_price', 'sale_price', 'min_stock_level', 'reorder_target'];
    if (numericFields.some((field) => formData[field] === '' || String(formData[field]).startsWith('-'))) {
      return 'Les quantités et les prix doivent être positifs ou nuls.';
    }
    if (
      isLowStock(formData.reorder_target, formData.min_stock_level) &&
      !isLowStock(formData.min_stock_level, formData.reorder_target)
    ) {
      return 'La cible de réapprovisionnement doit être supérieure ou égale au minimum.';
    }
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const error = validate();
    setFormError(error);
    if (error) return;
    try {
      if (editMode) await updateInventoryItem(selectedItem.id, formData);
      else await createInventoryItem(formData);
      toast.success(editMode ? 'Article mis à jour' : 'Article ajouté');
      setFormOpen(false);
      setFormData(EMPTY_FORM);
      setEditMode(false);
      await fetchInventory();
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || 'Erreur lors de l’enregistrement');
    }
  };

  const openCreate = () => {
    setFormData(EMPTY_FORM);
    setEditMode(false);
    setSelectedItem(null);
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setFormData({
      name: item.name || '',
      supplier: item.supplier || '',
      unit: item.unit || 'unité',
      quantity: String(item.quantity ?? '0'),
      purchase_price: String(item.purchase_price ?? '0.00'),
      sale_price: String(item.sale_price ?? '0.00'),
      category: item.category || 'parts',
      min_stock_level: String(item.min_stock_level ?? '0'),
      reorder_target: String(item.reorder_target ?? item.min_stock_level ?? '0'),
    });
    setSelectedItem(item);
    setEditMode(true);
    setFormError('');
    setFormOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article?')) return;
    try {
      await deleteInventoryItem(id);
      toast.success('Article supprimé');
      await fetchInventory();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const openHistory = async (item) => {
    setMovementItem(item);
    setMovements([]);
    setMovementsLoading(true);
    try {
      const response = await getInventoryMovements(item.id);
      setMovements(response.data || []);
    } catch { toast.error('Impossible de charger les mouvements'); }
    finally { setMovementsLoading(false); }
  };

  const filteredItems = items.filter((item) => (
    (item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.supplier?.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (categoryFilter === 'all' || item.category === categoryFilter)
  ));
  const categoryLabel = (value) => categories.find((category) => category.value === value)?.label || value;
  const lowStockCount = items.filter((item) => isLowStock(item.quantity || '0', item.min_stock_level || '0')).length;
  const setField = (field, value) => setFormData((current) => ({ ...current, [field]: value }));

  if (loading) return <div className="flex items-center justify-center h-64">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">Inventaire</h1>
          <p className="text-slate-500">Gérez le stock, les prix de vente et le réapprovisionnement</p>
        </div>
        {canManage && <Button onClick={openCreate} className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8]">
          <Plus className="w-4 h-4 mr-2" />Ajouter un article
        </Button>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Package className="w-6 h-6 text-blue-600" />
          <div><p className="text-2xl font-bold">{items.length}</p><p className="text-sm text-slate-500">Articles</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <div><p className="text-2xl font-bold">{lowStockCount}</p><p className="text-sm text-slate-500">Stock faible</p></div>
        </CardContent></Card>
      </div>

      <Card><CardContent className="p-4 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input placeholder="Rechercher par nom ou fournisseur..." value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)} className="pl-10" />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-48"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toutes catégories</SelectItem>
            {categories.map((category) => <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      <Card><CardContent className="p-0"><div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Article</TableHead>{canSeeCosts && <TableHead>Fournisseur</TableHead>}<TableHead>Stock</TableHead>
            {canSeeCosts && <TableHead>Achat</TableHead>}<TableHead>Vente</TableHead>{canSeeCosts && <TableHead>Marge</TableHead>}
            <TableHead>Minimum / cible</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>{filteredItems.map((item) => {
            const low = isLowStock(item.quantity || '0', item.min_stock_level || '0');
            return <TableRow key={item.id}>
              <TableCell><div className="flex items-center gap-2">
                {low && <AlertTriangle className="w-4 h-4 text-red-500" />}
                <div><p className="font-medium">{item.name}</p><p className="text-xs text-slate-500">{categoryLabel(item.category)}</p></div>
              </div></TableCell>
              {canSeeCosts && <TableCell>{item.supplier || 'Fournisseur à déterminer'}</TableCell>}
              <TableCell><span className={low ? 'text-red-600 font-semibold' : ''}>
                {formatQuantity(item.quantity || '0')} {item.unit || 'unité'}
              </span>{low && <span className="block text-xs text-red-600">À commander</span>}</TableCell>
              {canSeeCosts && <TableCell>{formatMoney(item.purchase_price || '0')}</TableCell>}
              <TableCell>{formatMoney(item.sale_price || '0')}</TableCell>
              {canSeeCosts && <TableCell>{formatMoney(calculateMargin(item.sale_price || '0', item.purchase_price || '0'))}</TableCell>}
              <TableCell>{formatQuantity(item.min_stock_level || '0')} / {formatQuantity(item.reorder_target || '0')}</TableCell>
              <TableCell className="text-right"><div className="flex justify-end gap-2">
                {canSeeMovements && <Button variant="outline" size="sm" onClick={() => openHistory(item)} title="Historique"><History className="w-4 h-4" /></Button>}
                {canManage && <Button variant="outline" size="sm" onClick={() => openEdit(item)}><Edit className="w-4 h-4" /></Button>}
                {canDelete && <Button variant="outline" size="sm" onClick={() => remove(item.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>}
              </div></TableCell>
            </TableRow>;
          })}</TableBody>
        </Table>
      </div></CardContent></Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editMode ? 'Modifier l’article' : 'Ajouter un article'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Nom *</Label><Input value={formData.name} onChange={(e) => setField('name', e.target.value)} required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Catégorie</Label><Select value={formData.category} onValueChange={(value) => setField('category', value)}>
              <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{categories.map((c) =>
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Unité *</Label><Input value={formData.unit} onChange={(e) => setField('unit', e.target.value)} required /></div>
          </div>
          <div><Label>Fournisseur</Label><Input value={formData.supplier} onChange={(e) => setField('supplier', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Quantité *</Label><Input type="number" min="0" step="any" value={formData.quantity} onChange={(e) => setField('quantity', e.target.value)} required /></div>
            <div><Label>Minimum *</Label><Input type="number" min="0" step="any" value={formData.min_stock_level} onChange={(e) => setField('min_stock_level', e.target.value)} required /></div>
            <div><Label>Prix d’achat *</Label><Input type="number" min="0" step="0.01" value={formData.purchase_price} onChange={(e) => setField('purchase_price', e.target.value)} required /></div>
            <div><Label>Prix de vente *</Label><Input type="number" min="0" step="0.01" value={formData.sale_price} onChange={(e) => setField('sale_price', e.target.value)} required /></div>
          </div>
          <div><Label>Cible de réapprovisionnement *</Label><Input type="number" min="0" step="any"
            value={formData.reorder_target} onChange={(e) => setField('reorder_target', e.target.value)} required /></div>
          {formError && <p className="text-sm text-red-600" role="alert">{formError}</p>}
          <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Annuler</Button>
            <Button type="submit">{editMode ? 'Mettre à jour' : 'Ajouter'}</Button></div>
        </form>
      </DialogContent></Dialog>

      <Dialog open={Boolean(movementItem)} onOpenChange={(open) => !open && setMovementItem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Historique — {movementItem?.name}</DialogTitle></DialogHeader>
          {movementsLoading ? <p>Chargement…</p> : movements.length === 0 ? <p className="text-slate-500">Aucun mouvement.</p> :
            <div className="space-y-3">{movements.map((movement) => <div key={movement.id} className="border rounded-lg p-3">
              <div className="flex justify-between gap-3"><div><p className="font-medium">{movement.type}</p>
                <p className="text-sm text-slate-600">{movement.actor_name || 'Système'}
                  {movement.created_at ? ' · ' + new Date(movement.created_at).toLocaleString('fr-CA') : ''}</p></div>
                <div className="text-right"><p className="font-semibold">{formatQuantity(movement.quantity)} {movementItem?.unit}</p>
                  <p className="text-xs text-slate-500">Solde : {formatQuantity(movement.balance_after)}</p></div></div>
              {(movement.project_id || movement.service_request_id) && <p className="text-xs text-slate-500 mt-2">
                Dossier : {movement.project_id || movement.service_request_id}</p>}
              {(movement.justification || movement.reference) && <p className="text-sm mt-2">{movement.justification || movement.reference}</p>}
            </div>)}</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
